<template>
  <div class="token-counter" v-if="userInfo">
    <div class="token-header">
      <div class="token-icon">
        <font-awesome-icon icon="bolt" />
      </div>
      <div class="token-title">{{ $t('token_counter.tokens_per_day') }}</div>
    </div>
    
    <div class="token-stats">
      <div class="token-numbers">
        <span class="remaining">{{ userInfo.remaining_requests }}</span>
        <span class="divider">/</span>
        <span class="total">{{ dailyLimit }}</span>
      </div>
      
      <div class="token-progress">
        <div 
          class="progress-bar" 
          :style="{ width: progressPercentage + '%' }"
          :class="{ 'low': isLowTokens, 'critical': isCriticalTokens }"
        ></div>
      </div>
    </div>
    
    <div class="token-info">
      <div class="plan-label">{{ planLabel }}</div>
      <div class="token-warning" v-if="isCriticalTokens">
        {{ $t('token_counter.tokens_critical') }}
      </div>
      <div class="token-warning low" v-else-if="isLowTokens">
        {{ $t('token_counter.tokens_running_low') }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, defineExpose, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import userService from '@/services/userService';
import { useTokenManager } from '@/composables/useTokenManager';

const { t } = useI18n();
const toast = inject('toast');
const userInfo = ref(null);
const loading = ref(false);

// Используем менеджер токенов
const { registerTokenCounter, unregisterTokenCounter, initTokenData } = useTokenManager();

// Вычисляемые свойства
const dailyLimit = computed(() => {
  if (!userInfo.value) return 0;
  return userInfo.value.daily_limit || 0;
});

const progressPercentage = computed(() => {
  if (!userInfo.value || dailyLimit.value === 0) return 0;
  return Math.max(0, (userInfo.value.remaining_requests / dailyLimit.value) * 100);
});

const isLowTokens = computed(() => {
  return progressPercentage.value <= 25 && progressPercentage.value > 10;
});

const isCriticalTokens = computed(() => {
  return progressPercentage.value <= 10;
});

const planLabel = computed(() => {
  if (!userInfo.value) return '';
  const plan = userInfo.value.subscription_plan || 'basic';
  return t(`token_counter.plan_${plan}`);
});

// Получение информации о пользователе
const fetchUserInfo = async () => {
  // Токен и его проверка выполняются централизованно через axios-интерсептор

  try {
    loading.value = true;
    
    // Получаем только динамические данные (токены) с сервера
    const serverData = await userService.getUserTokens();
    
    // Используем только данные с сервера
    userInfo.value = {
      role: serverData.role || 'user',
      email: serverData.email,
      remaining_requests: serverData.remaining_requests,
      daily_limit: serverData.daily_limit,
      subscription_plan: serverData.subscription_plan,
      subscription_end_date: serverData.subscription_end_date
    };
  } catch (error) {
    console.error('Ошибка при получении информации о пользователе:', error);
    const message = error._meta?.message || error.response?.data?.message || 'Ошибка загрузки информации о пользователе'
    toast?.show(message, 'error')
  } finally {
    loading.value = false;
  }
};

// Экспонируем метод для обновления счетчика извне
const updateUserInfo = () => {
  fetchUserInfo();
};

// Метод для обновления через WebSocket
const updateFromWebSocket = (data) => {
  if (data && data.remaining_requests !== undefined) {
    userInfo.value = { ...userInfo.value, ...data };
  }
};

defineExpose({ updateUserInfo, updateFromWebSocket });

onMounted(async () => {
  // Инициализируем данные о токенах через менеджер
  await initTokenData();
  // Загружаем полную информацию о пользователе
  await fetchUserInfo();
  
  // Регистрируем компонент в менеджере токенов
  registerTokenCounter({ updateFromWebSocket });
});

onBeforeUnmount(() => {
  // Отменяем регистрацию компонента
  unregisterTokenCounter({ updateFromWebSocket });
});
</script>

<style scoped>
.token-counter {
  padding: 16px;
  background: rgba(var(--theme-text-rgb), 0.03);
  border-top: 1px solid rgba(var(--theme-border-rgb), 0.3);
  border-bottom: 1px solid rgba(var(--theme-border-rgb), 0.3);
  margin-bottom: 12px;
}

.token-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.token-icon {
  width: 20px;
  height: 20px;
  background: linear-gradient(135deg, var(--primary-color), var(--accent-color));
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 10px;
}

.token-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--theme-text);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.token-stats {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.token-numbers {
  display: flex;
  align-items: baseline;
  gap: 4px;
  font-weight: 700;
}

.remaining {
  font-size: 18px;
  color: var(--primary-color);
}

.divider {
  font-size: 14px;
  color: var(--secondary-text-color);
  font-weight: 400;
}

.total {
  font-size: 14px;
  color: var(--secondary-text-color);
}

.token-progress {
  flex: 1;
  height: 4px;
  background: rgba(var(--theme-border-rgb), 0.3);
  border-radius: 2px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: var(--primary-color);
  border-radius: 2px;
  transition: all 0.3s ease;
}

.progress-bar.low {
  background: var(--accent-color);
}

.progress-bar.critical {
  background: var(--danger-color);
}

.token-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.plan-label {
  font-size: 11px;
  color: var(--secondary-text-color);
  font-weight: 500;
}

.token-warning {
  font-size: 10px;
  font-weight: 600;
  color: var(--danger-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  animation: pulse 2s infinite;
}

.token-warning.low {
  color: var(--accent-color);
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

/* Адаптивность */
@media (max-width: 480px) {
  .token-counter {
    padding: 12px;
  }
  
  .token-numbers {
    font-size: 14px;
  }
  
  .remaining {
    font-size: 16px;
  }
}
</style>
